import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Background,
  ReactFlow,
  ReactFlowProvider,
  getNodesBounds,
  getViewportForBounds,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Node,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { ZoomIn, ZoomOut, Scan, Layout } from 'lucide-react'
import {
  FlowGraphData,
  type EntityNodeData,
  type MemoryNodeData,
} from '../../utils/graph-data'
import { Memory } from '../../hooks/use-memories'
import { MemoryCard } from './memory-card'
import { MemoryGraphNode } from './memory-graph-node'
import { MemoryGraphEntityNode } from './memory-graph-entity-node'
import { MemoryRelationEdge } from './memory-relation-edge'
import { MemoryGraphLegend } from './memory-graph-legend'
import { applyDagreLayout, applyForceLayout } from './memory-graph-layout'
import { withNodeDimensions, type GraphNode } from './memory-graph-collisions'
import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { cn } from '@/lib/utils'

const nodeTypes = {
  memory: MemoryGraphNode,
  entity: MemoryGraphEntityNode,
}

const edgeTypes = {
  relation: MemoryRelationEdge,
}

const NODE_COUNT_WARNING = 150
const FIT_VIEW_PADDING = 0.08
const FIT_VIEW_MAX_ZOOM = 1.25
const FIT_VIEW_MIN_ZOOM = 0.1
const FIT_VIEW_DURATION = 200

type LayoutMode = 'force' | 'hierarchical'

interface MemoryGraphProps {
  graphData: FlowGraphData
  onMemoryClick?: (memory: Memory) => void
  onMemoryEdit?: (memory: Memory) => void
  onMemoryDelete?: (memoryId: string) => void
  className?: string
}

function MemoryGraphCanvas({
  graphData,
  onMemoryClick,
  onMemoryEdit,
  onMemoryDelete,
  className,
}: MemoryGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { getNodes, setViewport, zoomIn, zoomOut } = useReactFlow()
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null)
  const [layout, setLayout] = useState<LayoutMode>('force')
  const [nodes, setNodes, onNodesChange] = useNodesState(graphData.nodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(graphData.edges)

  /** fitView skips unmounted nodes when onlyRenderVisibleElements is on; use layout dimensions instead. */
  const fitGraphToNodes = useCallback(
    (nodesToFit?: GraphNode[]) => {
      const dimensioned = withNodeDimensions(nodesToFit ?? (getNodes() as GraphNode[]))
      if (dimensioned.length === 0) return

      const bounds = getNodesBounds(dimensioned)
      const container = containerRef.current
      if (!container || bounds.width <= 0 || bounds.height <= 0) return

      const { x, y, zoom } = getViewportForBounds(
        bounds,
        container.clientWidth,
        container.clientHeight,
        FIT_VIEW_MIN_ZOOM,
        FIT_VIEW_MAX_ZOOM,
        FIT_VIEW_PADDING,
      )
      void setViewport({ x, y, zoom }, { duration: FIT_VIEW_DURATION })
    },
    [getNodes, setViewport],
  )

  const applyLayout = useCallback(
    (mode: LayoutMode, data: FlowGraphData) => {
      const container = containerRef.current
      const width = container?.clientWidth ?? 800
      const height = container?.clientHeight ?? 600

      const laidOut =
        mode === 'hierarchical'
          ? applyDagreLayout(data.nodes, data.edges, width, height)
          : applyForceLayout(data.nodes, data.edges, width, height)

      setNodes(laidOut)
      setEdges(data.edges)
      requestAnimationFrame(() => fitGraphToNodes(laidOut))
    },
    [setNodes, setEdges, fitGraphToNodes],
  )

  useEffect(() => {
    applyLayout(layout, graphData)
  }, [graphData, layout, applyLayout])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const ro = new ResizeObserver(() => {
      fitGraphToNodes()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [fitGraphToNodes])

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node<MemoryNodeData | EntityNodeData>) => {
      if (node.type === 'memory' && node.data && 'memory' in node.data) {
        const memory = node.data.memory
        setSelectedMemory(memory)
        onMemoryClick?.(memory)
      }
    },
    [onMemoryClick],
  )

  const handleLayoutChange = (newLayout: LayoutMode) => {
    setLayout(newLayout)
  }

  const memoryNodeCount = graphData.nodes.filter((n) => n.type === 'memory').length
  const showScaleWarning = memoryNodeCount > NODE_COUNT_WARNING

  return (
    <div className={cn('relative h-full w-full', className)}>
      <div ref={containerRef} className="absolute inset-0">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={handleNodeClick}
          onlyRenderVisibleElements
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={16} size={1} />
        </ReactFlow>
      </div>

      {showScaleWarning ? (
        <div className="pointer-events-none absolute inset-x-0 top-16 z-10 flex justify-center px-4">
          <p className="rounded-md border border-border/40 bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow-sm backdrop-blur">
            Showing {memoryNodeCount} memories — narrow filters for better performance
          </p>
        </div>
      ) : null}

      <MemoryGraphLegend />

      <div className="absolute bottom-4 right-4 z-10 flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => zoomIn()} title="Zoom in">
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => zoomOut()} title="Zoom out">
          <ZoomOut className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fitGraphToNodes()}
          title="Fit graph"
          aria-label="Fit graph to view"
        >
          <Scan className="h-4 w-4" />
        </Button>
        <Select value={layout} onValueChange={(v) => handleLayoutChange(v as LayoutMode)}>
          <SelectTrigger className="w-[120px] bg-background/85 backdrop-blur">
            <Layout className="mr-2 h-4 w-4" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="force">Force</SelectItem>
            <SelectItem value="hierarchical">Hierarchical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Dialog
        open={!!selectedMemory}
        onOpenChange={(open) => {
          if (!open) setSelectedMemory(null)
        }}
      >
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Memory details</DialogTitle>
          </DialogHeader>
          {selectedMemory ? (
            <MemoryCard
              memory={selectedMemory}
              onEdit={(memory) => {
                onMemoryEdit?.(memory)
                setSelectedMemory(null)
              }}
              onDelete={(memoryId) => {
                onMemoryDelete?.(memoryId)
                setSelectedMemory(null)
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export function MemoryGraph(props: MemoryGraphProps) {
  return (
    <ReactFlowProvider>
      <MemoryGraphCanvas {...props} />
    </ReactFlowProvider>
  )
}
